/**
 * DiAuditPanel — Auditoria e Sincronização de Cadernetas de Voo
 *
 * Permite ao administrador:
 *  1. Iniciar uma auditoria dry-run (sem alterar dados)
 *  2. Aplicar correcções automáticas seguras
 *  3. Ver o histórico de auditorias anteriores
 *  4. Consultar os totais de horas por piloto e instrutor
 *  5. Gerar relatórios ANAC (Dossier 1 — Controlo Operacional / Dossier 2 — Formação)
 */
import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { isFleetDomain } from "../../config/productConfig";
import { toast } from "sonner";
import { RefreshCw, Wrench, CheckCircle, AlertTriangle, XCircle, Clock, Users, BookOpen, ChevronDown, ChevronRight, ShieldCheck, Download } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
// Shared API base: always the current host, so calls go through the same domain
// (and the fleet Cloudflare Worker) as the rest of the app, never straight to utc.com.pt.
import { API } from "../../App";


// ── helpers ──────────────────────────────────────────────────────────────────

function fmtMin(min) {
  if (!min && min !== 0) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}:${m.toString().padStart(2, "0")}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

const ISSUE_LABELS = {
  broken_companion_ref:    { label: "Referência inválida",       color: "text-red-400",    bg: "bg-red-400/10" },
  stale_companion_name:    { label: "Nome desatualizado",        color: "text-yellow-400", bg: "bg-yellow-400/10" },
  companion_kind_mismatch: { label: "Tipo errado",               color: "text-orange-400", bg: "bg-orange-400/10" },
  missing_companion_kind:  { label: "Tipo ausente",              color: "text-orange-400", bg: "bg-orange-400/10" },
  companion_id_missing:    { label: "ID ausente",                color: "text-amber-400",  bg: "bg-amber-400/10" },
  invalid_companion_kind:  { label: "Tipo inválido",             color: "text-red-400",    bg: "bg-red-400/10"  },
  orphan_actor:            { label: "Ator eliminado",            color: "text-red-500",    bg: "bg-red-500/10" },
  headless_flight:         { label: "Voo sem ator",              color: "text-red-500",    bg: "bg-red-500/10" },
  duplicate_flight:        { label: "Duplicado (conflito)",      color: "text-purple-400", bg: "bg-purple-400/10" },
  duplicate_actor_entry:   { label: "Duplicado (mesmo ator)",    color: "text-purple-400", bg: "bg-purple-400/10" },
  missing_nav_svc:         { label: "Sem nº diário/serviço",     color: "text-amber-500",  bg: "bg-amber-500/10" },
};

// ── sub-components ────────────────────────────────────────────────────────────

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <div className={`flex items-center gap-3 rounded-xl border border-white/10 bg-slate-800/60 p-4`}>
      <div className={`rounded-lg p-2 ${color}`}>
        <Icon size={18} className="opacity-80" />
      </div>
      <div>
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-xs text-slate-400">{label}</p>
      </div>
    </div>
  );
}

function IssueRow({ issue }) {
  const meta = ISSUE_LABELS[issue.kind] || { label: issue.kind, color: "text-slate-400", bg: "bg-slate-700" };
  return (
    <tr className="border-t border-white/5 hover:bg-white/5 transition-colors">
      <td className="px-3 py-2.5">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${meta.bg} ${meta.color}`}>
          {meta.label}
        </span>
      </td>
      <td className="px-3 py-2.5 font-mono text-xs text-slate-400">{issue.navigation_log_number}/{issue.service_number}</td>
      <td className="px-3 py-2.5 text-xs text-slate-400">{issue.flight_date}</td>
      <td className="px-3 py-2.5 text-xs text-slate-300 max-w-xs">{issue.description}</td>
      <td className="px-3 py-2.5">
        {issue.auto_fixable ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
            <CheckCircle size={12} /> Auto-corrigível
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-amber-400">
            <AlertTriangle size={12} /> Revisão manual
          </span>
        )}
      </td>
    </tr>
  );
}

function IssuesTable({ issues }) {
  const [filter, setFilter] = useState("all");
  const kinds = [...new Set(issues.map(i => i.kind))];
  const filtered = filter === "all" ? issues : issues.filter(i => i.kind === filter);

  return (
    <div>
      {/* Filter bar */}
      <div className="mb-3 flex flex-wrap gap-2">
        <button
          onClick={() => setFilter("all")}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${filter === "all" ? "bg-blue-600 text-white" : "bg-slate-700 text-slate-300 hover:bg-slate-600"}`}
        >
          Todos ({issues.length})
        </button>
        {kinds.map(k => {
          const meta = ISSUE_LABELS[k] || { label: k, color: "text-slate-400" };
          const n = issues.filter(i => i.kind === k).length;
          return (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${filter === k ? "bg-slate-500 text-white" : "bg-slate-700 text-slate-300 hover:bg-slate-600"}`}
            >
              {meta.label} ({n})
            </button>
          );
        })}
        <button
          onClick={() => setFilter("fixable")}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${filter === "fixable" ? "bg-emerald-700 text-white" : "bg-slate-700 text-slate-300 hover:bg-slate-600"}`}
        >
          Auto-corrigível ({issues.filter(i => i.auto_fixable).length})
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-800 text-slate-400 text-xs uppercase tracking-wide">
              <th className="px-3 py-2.5 text-left font-medium">Tipo</th>
              <th className="px-3 py-2.5 text-left font-medium">Diário/Serv.</th>
              <th className="px-3 py-2.5 text-left font-medium">Data</th>
              <th className="px-3 py-2.5 text-left font-medium">Descrição</th>
              <th className="px-3 py-2.5 text-left font-medium">Ação</th>
            </tr>
          </thead>
          <tbody>
            {(filter === "fixable" ? issues.filter(i => i.auto_fixable) : filtered).map((issue, idx) => (
              <IssueRow key={`${issue.flight_id}-${idx}`} issue={issue} />
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="py-8 text-center text-slate-500 text-sm">Nenhum problema encontrado nesta categoria.</div>
        )}
      </div>
    </div>
  );
}

function PilotTotalsTable({ pilots }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? pilots : pilots.slice(0, 8);
  if (!pilots?.length) return <p className="text-slate-500 text-sm">Nenhum piloto registado.</p>;

  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-800 text-slate-400 text-xs uppercase tracking-wide">
              <th className="px-3 py-2.5 text-left font-medium">Piloto</th>
              <th className="px-3 py-2.5 text-left font-medium">Tipo</th>
              <th className="px-3 py-2.5 text-right font-medium">Voos PIC</th>
              <th className="px-3 py-2.5 text-right font-medium">Voos DC</th>
              <th className="px-3 py-2.5 text-right font-medium">Total</th>
              <th className="px-3 py-2.5 text-right font-medium">Horas PIC</th>
              <th className="px-3 py-2.5 text-right font-medium">Horas DC</th>
              <th className="px-3 py-2.5 text-right font-medium">Último voo</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(p => (
              <tr key={p.pilot_id} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                <td className="px-3 py-2.5 text-white font-medium">{p.name}</td>
                <td className="px-3 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${p.kind === "student" ? "bg-blue-400/15 text-blue-300" : p.kind === "avulso" ? "bg-slate-400/15 text-slate-400" : "bg-emerald-400/15 text-emerald-300"}`}>
                    {p.kind === "student" ? "Aluno" : p.kind === "avulso" ? "Avulso" : "Regular"}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right text-slate-300">{p.flights_as_actor}</td>
                <td className="px-3 py-2.5 text-right text-slate-300">{p.flights_as_companion}</td>
                <td className="px-3 py-2.5 text-right font-semibold text-white">{p.total_flights}</td>
                <td className="px-3 py-2.5 text-right text-slate-300 font-mono text-xs">{fmtMin(p.pic_minutes)}</td>
                <td className="px-3 py-2.5 text-right text-emerald-400 font-mono text-xs">{fmtMin(p.dc_minutes)}</td>
                <td className="px-3 py-2.5 text-right text-slate-400 text-xs">{fmtDate(p.last_flight)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pilots.length > 8 && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="mt-2 flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {expanded ? "Mostrar menos" : `Ver mais ${pilots.length - 8} pilotos`}
        </button>
      )}
    </div>
  );
}

function InstructorTotalsTable({ instructors }) {
  if (!instructors?.length) return <p className="text-slate-500 text-sm">Nenhum instrutor registado.</p>;
  return (
    <div className="overflow-x-auto rounded-xl border border-white/10">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-800 text-slate-400 text-xs uppercase tracking-wide">
            <th className="px-3 py-2.5 text-left font-medium">Instrutor</th>
            <th className="px-3 py-2.5 text-left font-medium">PU</th>
            <th className="px-3 py-2.5 text-left font-medium">Lic.</th>
            <th className="px-3 py-2.5 text-right font-medium">Voos</th>
            <th className="px-3 py-2.5 text-right font-medium">Horas PIC</th>
            <th className="px-3 py-2.5 text-right font-medium">Horas Instr.</th>
            <th className="px-3 py-2.5 text-right font-medium">Último voo</th>
          </tr>
        </thead>
        <tbody>
          {instructors.map(i => (
            <tr key={i.instructor_id} className="border-t border-white/5 hover:bg-white/5 transition-colors">
              <td className="px-3 py-2.5 text-white font-medium">{i.name}</td>
              <td className="px-3 py-2.5 text-slate-400 font-mono text-xs">{i.pu_number}</td>
              <td className="px-3 py-2.5">
                <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${i.license_status === "R" ? "bg-amber-400/15 text-amber-300" : "bg-emerald-400/15 text-emerald-300"}`}>
                  {i.license_status || "S"}
                </span>
              </td>
              <td className="px-3 py-2.5 text-right font-semibold text-white">{i.total_flights}</td>
              <td className="px-3 py-2.5 text-right text-slate-300 font-mono text-xs">{fmtMin(i.pic_minutes)}</td>
              <td className="px-3 py-2.5 text-right text-blue-400 font-mono text-xs">{fmtMin(i.instructor_minutes)}</td>
              <td className="px-3 py-2.5 text-right text-slate-400 text-xs">{fmtDate(i.last_flight)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HistoryList({ history }) {
  if (!history?.length) return <p className="text-slate-500 text-sm">Nenhuma auditoria anterior registada.</p>;
  return (
    <div className="space-y-2">
      {history.map(h => (
        <div key={h.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-slate-800/40 px-4 py-3">
          <div className="flex items-center gap-3">
            {h.fix_mode ? (
              <Wrench size={15} className="text-emerald-400" />
            ) : (
              <RefreshCw size={15} className="text-blue-400" />
            )}
            <div>
              <p className="text-sm text-white">
                {h.fix_mode ? "Correção aplicada" : "Auditoria"} — {h.scanned_flights} voos analisados
              </p>
              <p className="text-xs text-slate-400">{fmtDate(h.run_at)} {h.run_at?.slice(11, 16)}</p>
            </div>
          </div>
          <div className="flex gap-4 text-right">
            {h.summary?.total_issues > 0 ? (
              <div>
                <p className="text-sm font-semibold text-amber-400">{h.summary.total_issues}</p>
                <p className="text-xs text-slate-500">problemas</p>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-emerald-400 text-sm">
                <CheckCircle size={14} /> Sem problemas
              </div>
            )}
            {h.summary?.fixed_count > 0 && (
              <div>
                <p className="text-sm font-semibold text-emerald-400">{h.summary.fixed_count}</p>
                <p className="text-xs text-slate-500">corrigidos</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function DiAuditPanel() {
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [marking, setMarking] = useState(false);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("issues");

  const runAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/di/sync/audit`, {}, { withCredentials: true });
      setReport(data);
      setActiveTab("issues");
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao iniciar auditoria");
    } finally {
      setLoading(false);
    }
  }, []);

  const applyFix = useCallback(async () => {
    setFixing(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/di/sync/fix`, {}, { withCredentials: true });
      setReport(data);
      setActiveTab("fixes");
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao aplicar correcções");
    } finally {
      setFixing(false);
    }
  }, []);

  const markInvalid = useCallback(async () => {
    setMarking(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/di/sync/mark-invalid`, {}, { withCredentials: true });
      setReport(data);
      setActiveTab("issues");
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao marcar inválidos");
    } finally {
      setMarking(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/di/sync/history`, { withCredentials: true });
      setHistory(data);
    } catch (e) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const nAutoFixable = report?.summary?.auto_fixable ?? 0;
  const hasIssues = (report?.summary?.total_issues ?? 0) > 0;

  return (
    <div className="space-y-6 p-1" data-testid="di-audit-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-white">Auditoria de Cadernetas</h2>
          <p className="mt-1 text-sm text-slate-400">
            Verifica a coerência entre os voos das aeronaves, os diários dos pilotos e os diários dos instrutores.
            Detecta e corrige automaticamente referências inválidas, nomes desatualizados e tipos errados de acompanhante.
          </p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3">
        <button
          data-testid="di-audit-run-btn"
          onClick={runAudit}
          disabled={loading}
          className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50 transition-colors"
        >
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          {loading ? "A analisar..." : "Iniciar Auditoria"}
        </button>

        {nAutoFixable > 0 && !report?.fix_mode && (
          <button
            data-testid="di-audit-fix-btn"
            onClick={applyFix}
            disabled={fixing}
            className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50 transition-colors"
          >
            <Wrench size={15} className={fixing ? "animate-spin" : ""} />
            {fixing ? "A corrigir..." : `Corrigir automaticamente (${nAutoFixable})`}
          </button>
        )}

        {(report?.summary?.needs_admin_review ?? 0) > 0 && !report?.mark_invalid_mode && (
          <button
            data-testid="di-audit-mark-invalid-btn"
            onClick={markInvalid}
            disabled={marking}
            className="flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm font-medium text-amber-300 hover:bg-amber-500/20 disabled:opacity-50 transition-colors"
          >
            <AlertTriangle size={15} className={marking ? "animate-spin" : ""} />
            {marking ? "A marcar..." : `Marcar inválidos (${report.summary.needs_admin_review})`}
          </button>
        )}

        <button
          data-testid="di-audit-history-btn"
          onClick={loadHistory}
          disabled={historyLoading}
          className="flex items-center gap-2 rounded-xl border border-white/10 bg-slate-700 px-4 py-2.5 text-sm font-medium text-slate-200 hover:bg-slate-600 disabled:opacity-50 transition-colors"
        >
          <Clock size={15} />
          {historyLoading ? "A carregar..." : "Histórico"}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          <XCircle size={15} /> {error}
        </div>
      )}

      {/* Report */}
      {report && (
        <div className="space-y-5">
          {/* Summary */}
          {report.fix_mode && report.summary.fixed_count > 0 && (
            <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-400">
              <CheckCircle size={15} /> {report.summary.fixed_count} problema(s) corrigido(s) automaticamente
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCard icon={BookOpen} label="Voos analisados" value={report.scanned_flights} color="bg-blue-500/15 text-blue-400" />
            <SummaryCard icon={AlertTriangle} label="Problemas totais" value={report.summary.total_issues} color={report.summary.total_issues > 0 ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"} />
            <SummaryCard icon={Wrench} label="Auto-corrigíveis" value={report.summary.auto_fixable} color="bg-emerald-500/15 text-emerald-400" />
            <SummaryCard icon={Users} label="Revisão manual" value={report.summary.needs_admin_review} color={report.summary.needs_admin_review > 0 ? "bg-red-500/15 text-red-400" : "bg-slate-500/15 text-slate-400"} />
          </div>

          {!hasIssues && (
            <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-400">
              <CheckCircle size={15} /> Sem problemas detectados — todas as cadernetas estão coerentes.
            </div>
          )}

          {/* Tabs */}
          {(hasIssues || report.pilot_totals?.length > 0 || report.instructor_totals?.length > 0) && (
            <div>
              <div className="flex border-b border-white/10 mb-4">
                {hasIssues && (
                  <button
                    onClick={() => setActiveTab("issues")}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "issues" ? "border-amber-400 text-amber-400" : "border-transparent text-slate-400 hover:text-white"}`}
                  >
                    Problemas ({report.summary.total_issues})
                  </button>
                )}
                {report.pilot_totals?.length > 0 && (
                  <button
                    onClick={() => setActiveTab("pilots")}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "pilots" ? "border-blue-400 text-blue-400" : "border-transparent text-slate-400 hover:text-white"}`}
                  >
                    Pilotos ({report.pilot_totals.length})
                  </button>
                )}
                {report.instructor_totals?.length > 0 && (
                  <button
                    onClick={() => setActiveTab("instructors")}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "instructors" ? "border-purple-400 text-purple-400" : "border-transparent text-slate-400 hover:text-white"}`}
                  >
                    Instrutores ({report.instructor_totals.length})
                  </button>
                )}
                {report.fix_mode && report.fixes_applied?.length > 0 && (
                  <button
                    onClick={() => setActiveTab("fixes")}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "fixes" ? "border-emerald-400 text-emerald-400" : "border-transparent text-slate-400 hover:text-white"}`}
                  >
                    Corrigido ({report.fixes_applied.length})
                  </button>
                )}
              </div>

              {activeTab === "issues" && hasIssues && <IssuesTable issues={report.issues} />}

              {activeTab === "pilots" && <PilotTotalsTable pilots={report.pilot_totals} />}

              {activeTab === "instructors" && <InstructorTotalsTable instructors={report.instructor_totals} />}

              {activeTab === "fixes" && (
                <div className="overflow-x-auto rounded-xl border border-white/10">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-800 text-slate-400 text-xs uppercase tracking-wide">
                        <th className="px-3 py-2.5 text-left">Voo</th>
                        <th className="px-3 py-2.5 text-left">Tipo de problema</th>
                        <th className="px-3 py-2.5 text-left">Correcção aplicada</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.fixes_applied.map((fix, idx) => (
                        <tr key={idx} className="border-t border-white/5">
                          <td className="px-3 py-2.5 font-mono text-xs text-slate-400">{fix.flight_id}</td>
                          <td className="px-3 py-2.5 text-xs text-slate-300">{ISSUE_LABELS[fix.issue_kind]?.label || fix.issue_kind}</td>
                          <td className="px-3 py-2.5 text-xs text-emerald-400">{fix.fix}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* History */}
      {history !== null && (
        <div>
          <h3 className="mb-3 text-sm font-semibold text-slate-300 flex items-center gap-2">
            <Clock size={14} /> Histórico de Auditorias
          </h3>
          <HistoryList history={history} />
        </div>
      )}

      {/* ── Relatórios ANAC ──────────────────────────────────────────── */}
      <AnacReportsSection />
    </div>
  );
}



// ── ANAC Reports sub-component ───────────────────────────────────────────────

function AnacReportsSection() {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("__all__");
  const [anac1Loading, setAnac1Loading] = useState(false);
  const [anac2Loading, setAnac2Loading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/di/reports/classes`, { withCredentials: true })
      .then(r => setClasses(r.data || []))
      .catch(() => {});
  }, []);

  const downloadAnac1 = async () => {
    setAnac1Loading(true);
    try {
      const r = await axios.get(`${API}/di/reports/anac-compliance?download=true`, {
        withCredentials: true, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `anac_controlo_operacional_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Dossier 1 descarregado");
    } catch (e) { toast.error("Erro ao gerar Dossier 1"); }
    finally { setAnac1Loading(false); }
  };

  const downloadAnac2 = async () => {
    setAnac2Loading(true);
    try {
      const classParam = (selectedClass && selectedClass !== "__all__") ? `&class_id=${selectedClass}` : "";
      const r = await axios.get(`${API}/di/reports/student-progress?download=true${classParam}`, {
        withCredentials: true, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `anac_formacao_alunos_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Dossier 2 descarregado");
    } catch (e) { toast.error("Erro ao gerar Dossier 2"); }
    finally { setAnac2Loading(false); }
  };

  return (
    <Card className="instrument-panel mt-6" data-testid="di-anac-reports-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-orange-400" /> Relatórios ANAC
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-[11px] text-slate-400">
          PDFs gerados em tempo real com os dados actuais da plataforma. Descarregados imediatamente após geração.
        </div>

        <div className="border border-cockpit-border rounded p-3 space-y-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-orange-300" />
            <div>
              <div className="text-xs font-semibold text-slate-200">Dossier 1 — Controlo Operacional</div>
              <div className="text-[10px] text-slate-400">Instrutores · Aeronaves · Documentos · Manutenções · Seguros · Alertas</div>
            </div>
          </div>
          <Button size="sm" onClick={downloadAnac1} disabled={anac1Loading} data-testid="di-anac1-btn" className="btn-cockpit text-xs">
            <Download className="w-3.5 h-3.5 mr-1" />
            {anac1Loading ? "A gerar PDF…" : "Descarregar Dossier 1"}
          </Button>
        </div>

        {/* Student reports only exist on the training product: Aerofleet has no students. */}
        {!isFleetDomain() && (
          <div className="border border-cockpit-border rounded p-3 space-y-2">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-cyan-300" />
              <div>
                <div className="text-xs font-semibold text-slate-200">Dossier 2 — Formação de Alunos</div>
                <div className="text-[10px] text-slate-400">Exames realizados · Resultados · Correção completa</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger className="h-8 text-xs bg-slate-800 border-slate-700 w-48" data-testid="di-anac2-class-select">
                  <SelectValue placeholder="Escola toda" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="__all__">Escola toda</SelectItem>
                  {classes.map(c => (
                    <SelectItem key={c.class_id} value={c.class_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={downloadAnac2} disabled={anac2Loading} data-testid="di-anac2-btn" className="btn-cockpit text-xs">
                <Download className="w-3.5 h-3.5 mr-1" />
                {anac2Loading ? "A gerar PDF…" : "Descarregar Dossier 2"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
